import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { useAtom, useAtomValue } from 'jotai';

import { Badge } from '~shared/components/Badge';
import { Container } from '~shared/components/Container/Container';
import { Divider } from '~shared/components/Divider';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { TooltipV2 } from '~shared/components/TooltipV2';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { OneAttemptPaywall } from '~teacher/components/OneAttemptPaywall';
import { usePaywallReducer } from '~teacher/components/PaywallDialog';
import {
  deliveryMethodAtom,
  oneAttemptAtom,
  randomAnswersAtom,
  randomQuestionsAtom,
  requireNamesAtom,
  showFeedbackAtom,
  showResultsAtom,
} from '~teacher/launchv2/atoms';
import { useIsRoomRostered } from '~teacher/launchv2/context/RoomContext';
import { DeliveryMethod } from '~teacher/launchv2/DeliveryMethod';
import { proCheck } from '~teacher/util/proCheck';

export function ToggleSettings() {
  const [requireNames, setRequireNames] = useAtom(requireNamesAtom);
  const [randomAnswers, setRandomAnswers] = useAtom(randomAnswersAtom);
  const [randomQuestions, setRandomQuestions] = useAtom(randomQuestionsAtom);
  const [showFeedback, setShowFeedback] = useAtom(showFeedbackAtom);
  const [showResults, setShowResults] = useAtom(showResultsAtom);
  const [oneAttempt, setOneAttempt] = useAtom(oneAttemptAtom);
  const isRoomRostered = useIsRoomRostered();
  const deliveryMethod = useAtomValue(deliveryMethodAtom);

  const applicationContext = useTeacherApplicationContext();

  return (
    <div className="flex flex-col">
      <Container margin={'0 0 10px 0'}>
        <Divider />
      </Container>
      {applicationContext ===
      TeacherApplicationContext.ShowbieAssessments ? null : (
        <TooltipV2
          isDisabled={!isRoomRostered}
          label={translate('Student names are required in rooms with rosters.')}
        >
          <div>
            <SettingsToggle
              on={requireNames}
              isDisabled={isRoomRostered}
              onChange={(newValue) => {
                setRequireNames(newValue);
              }}
            >
              {translate('Require Names')}
            </SettingsToggle>
            <Container margin={'10px 0'}>
              <Divider />
            </Container>
          </div>
        </TooltipV2>
      )}
      <TooltipV2
        isDisabled={deliveryMethod !== DeliveryMethod.TeacherPaced}
        label={translate(
          'Questions are delivered in the same order to all students.'
        )}
      >
        <div>
          <SettingsToggle
            on={randomQuestions}
            isDisabled={deliveryMethod === DeliveryMethod.TeacherPaced}
            onChange={(newValue) => {
              setRandomQuestions(newValue);
            }}
          >
            {translate('Shuffle Questions')}
          </SettingsToggle>
          <Container margin={'10px 0'}>
            <Divider />
          </Container>
        </div>
      </TooltipV2>
      <div>
        <SettingsToggle
          on={randomAnswers}
          onChange={(newValue) => {
            setRandomAnswers(newValue);
          }}
        >
          {translate('Shuffle Answers')}
        </SettingsToggle>
        <Container margin={'10px 0'}>
          <Divider />
        </Container>
      </div>
      <TooltipV2
        isDisabled={deliveryMethod !== DeliveryMethod.OpenNavigation}
        label={translate(
          'Feedback is disabled because students can change answers before submitting.'
        )}
      >
        <div>
          <SettingsToggle
            on={showFeedback}
            isDisabled={deliveryMethod === DeliveryMethod.OpenNavigation}
            onChange={(newValue) => {
              setShowFeedback(newValue);
            }}
          >
            {translate('Show Question Feedback')}
          </SettingsToggle>
          <Container margin={'10px 0'}>
            <Divider />
          </Container>
        </div>
      </TooltipV2>
      <div>
        <SettingsToggle
          on={showResults}
          onChange={(newValue) => {
            setShowResults(newValue);
          }}
        >
          {translate('Show Final Score')}
        </SettingsToggle>
        <Container margin={'10px 0'}>
          <Divider />
        </Container>
      </div>
      <div>
        <OneAttemptToggle
          on={oneAttempt}
          isDisabled={!isRoomRostered}
          isRostered={isRoomRostered}
          onChange={(newValue) => {
            setOneAttempt(newValue);
          }}
        >
          {translate('One Attempt')}
        </OneAttemptToggle>
      </div>
    </div>
  );
}

interface SettingsToggleProps {
  on: boolean;
  onChange: (newValue: boolean) => void;
  isDisabled?: boolean;
  children: string;
}

function SettingsToggle({
  on,
  onChange,
  isDisabled,
  children,
}: SettingsToggleProps) {
  return (
    <label
      aria-label={children}
      className="flex justify-between items-center cursor-pointer overflow-hidden py-2"
    >
      <p
        className={cx(
          'text-sm font-400',
          isDisabled ? 'text-grey-500' : 'text-blue-900'
        )}
      >
        {children}
      </p>
      <ToggleSwitch
        size="small"
        on={on}
        disabled={isDisabled}
        onChange={onChange}
      />
    </label>
  );
}

interface OneAttemptToggleProps {
  on: boolean;
  onChange: (newValue: boolean) => void;
  isDisabled?: boolean;
  children: React.ReactNode;
  isRostered: boolean;
}
function OneAttemptToggle({
  on,
  onChange,
  isDisabled,
  children,
  isRostered,
}: OneAttemptToggleProps) {
  const [innerTooltip, setInnerTooltip] = React.useState(false);

  const [{ open: paywallOpen }, dispatch] = usePaywallReducer();

  const label = translate(
    'Students can only take the quiz once. If they leave and rejoin the room, they will continue the quiz where they left off.'
  );
  const getDisabledTooltip = () => {
    if (proCheck.canAddRoster()) {
      return translate('This setting is only available in rooms with rosters');
    } else {
      return translate('One attempt is a pro feature');
    }
  };
  const handleOneAttemptChanged = React.useCallback(
    (newVal: boolean) => {
      if (!proCheck.hasPaidPlan()) {
        dispatch({ type: 'OPEN' });
        return;
      }
      onChange(newVal);
    },
    [onChange, dispatch]
  );
  return (
    <>
      <TooltipV2
        label={getDisabledTooltip()}
        isDisabled={isRostered || innerTooltip}
      >
        <label
          aria-label={label}
          className="flex justify-between items-center cursor-pointer overflow-hidden py-2"
        >
          <Container row verticalCenter>
            <p
              className={cx(
                'text-sm font-400 whitespace-no-wrap mr-2 text-blue-900'
              )}
            >
              {children}
            </p>
            {!proCheck.canAddRoster() ? (
              <Badge className={'mr-2 '} tone={'pro'}>
                PRO
              </Badge>
            ) : null}
            <TooltipV2 label={label}>
              <AttemptSvg
                onMouseEnter={() => setInnerTooltip(true)}
                onMouseLeave={() => setInnerTooltip(false)}
              />
            </TooltipV2>
          </Container>
          <ToggleSwitch
            size="small"
            on={on}
            disabled={isDisabled && proCheck.hasPaidPlan()}
            onChange={handleOneAttemptChanged}
          />
        </label>
      </TooltipV2>
      <OneAttemptPaywall
        open={paywallOpen}
        onClose={() => dispatch({ type: 'CLOSE' })}
      />
    </>
  );
}

const AttemptSvg = React.forwardRef<
  SVGSVGElement,
  { onMouseEnter: () => void; onMouseLeave: () => void }
>((props, ref) => {
  return (
    <svg
      ref={ref}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect width="18" height="18" fill="white" fillOpacity="0.01" />
      <rect width="18" height="18" rx="9" fill="#0099C7" />
      <path
        d="M7.85866 14V6.90909H9.52983V14H7.85866ZM8.69886 5.9027C8.43419 5.9027 8.20644 5.81499 8.01562 5.63956C7.82481 5.46106 7.7294 5.24716 7.7294 4.99787C7.7294 4.7455 7.82481 4.5316 8.01562 4.35618C8.20644 4.17767 8.43419 4.08842 8.69886 4.08842C8.96662 4.08842 9.19437 4.17767 9.3821 4.35618C9.57292 4.5316 9.66832 4.7455 9.66832 4.99787C9.66832 5.24716 9.57292 5.46106 9.3821 5.63956C9.19437 5.81499 8.96662 5.9027 8.69886 5.9027Z"
        fill="white"
      />
    </svg>
  );
});
AttemptSvg.displayName = 'AttemptSvg';
